import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiPlusLight,
  PiTrashLight,
  PiPencilSimpleLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import {
  deleteGlobalRule,
} from '../../store/actions/Review/ReviewActions';
import { GlobalRule, FindingSeverity } from '../../types/review';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, globalRulesTourSteps } from '../shared/FeatureTour';
import GlassCard from '../shared/brand/GlassCard';

const severityColors: Record<FindingSeverity, BadgeColor> = {
  critical: 'red',
  major: 'orange',
  minor: 'yellow',
  info: 'blue',
};

/** The card's left accent - the same token ramps the clause-rule risk bands use. */
const severityAccent: Record<FindingSeverity, string> = {
  critical: 'border-l-danger-400 dark:border-l-danger-600',
  major: 'border-l-warning-500 dark:border-l-warning-600',
  minor: 'border-l-warning-300 dark:border-l-warning-500',
  info: 'border-l-info-400 dark:border-l-info-600',
};

const severityLabels: Record<FindingSeverity, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  info: 'Info',
};

interface ReviewGlobalRulesProps {
  reviewUuid: string;
  globalRules: GlobalRule[];
  readOnly?: boolean;
}

const ReviewGlobalRules: React.FC<ReviewGlobalRulesProps> = ({ reviewUuid, globalRules, readOnly }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<FindingSeverity | 'all'>('all');

  /* Search + severity filter, the clause-rules bar's equivalents - and the
     same order the severity badges rank in, most serious first. */
  const filteredRules = useMemo(() => {
    const rank: Record<FindingSeverity, number> = { critical: 0, major: 1, minor: 2, info: 3 };
    let rules = [...globalRules].sort((a, b) => (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rules = rules.filter((r) => (r.rule_text || '').toLowerCase().includes(q));
    }
    if (severityFilter !== 'all') rules = rules.filter((r) => r.severity === severityFilter);
    return rules;
  }, [globalRules, searchQuery, severityFilter]);

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'global-rules',
    steps: globalRulesTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  // ─── Side panel handlers ─────────────────────────────────────────────

  const handleAddRule = useCallback(() => {
    dispatch(showSidePanel({
      type: 'editGlobalRule',
      showSidePanel: true,
      size: 'md',
      message: t('New global rule'),
      reviewUuid,
      actions: [
        { label: t('Save rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleEdit = useCallback((rule: GlobalRule) => {
    dispatch(showSidePanel({
      type: 'editGlobalRule',
      showSidePanel: true,
      size: 'md',
      message: t('Edit global rule'),
      reviewUuid,
      rule,
      actions: [
        { label: t('Update rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleDelete = useCallback((ruleUuid: string) => {
    dispatch(
      showModal({
        type: 'confirmation',
        title: t('Delete global rule'),
        message: t('This will permanently remove this global rule from the playbook. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        variant: 'danger',
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteGlobalRule(reviewUuid, ruleUuid) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  return (
    <div className="pb-10 space-y-0">
      {/* Filter bar with add button */}
      <div data-tour-id="global-rules-header" className="pt-4 px-2">
        <FilterBarWithTabs
          onSearchChange={setSearchQuery}
          filters={[
            {
              id: 'severity',
              name: t('Severity'),
              options: (Object.keys(severityLabels) as FindingSeverity[]).map((key) => ({
                value: key,
                label: t(severityLabels[key]),
              })),
            },
          ]}
          selectedFilters={{ severity: severityFilter !== 'all' ? [severityFilter] : [] }}
          onFilterChange={(_name, values) =>
            setSeverityFilter(values.length > 0 ? (values[0] as FindingSeverity) : 'all')}
          actions={!readOnly ? [
            {
              label: t('Add rule'),
              onClick: handleAddRule,
              icon: <PiPlusLight className="w-4 h-4" />,
            },
          ] : []}
          noParentPadding
          noBorder
        />
      </div>

      <div className="px-4 pt-4 space-y-2">
      {/* Rules list */}
      {globalRules.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-hairline rounded-lg">
          <p className="text-sm text-fg-muted">
            {readOnly
              ? t('No global rules defined for this playbook.')
              : t('No global rules yet. Add cross-cutting rules that apply to the entire agreement.')}
          </p>
          {!readOnly && (
            <Button
              variant="neutral"
              size="small"
              className="mt-3"
              onClick={handleAddRule}
            >
              <PiPlusLight className="w-4 h-4 me-1" />
              {t('Add first rule')}
            </Button>
          )}
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-fg-muted">{t('No rules match your search.')}</p>
        </div>
      ) : (
        <div className="space-y-2" data-tour-id="global-rules-list">
          {filteredRules.map((rule) => (
            /*
              The same card a clause rule is: a GlassCard with the severity
              as its left accent, the badge beside the text, and labelled
              outline Edit / Delete buttons on the right. Two kinds of rule
              in one playbook should not read as two different products.
            */
            <GlassCard
              key={rule.uuid}
              padding="none"
              className={clsx(
                'overflow-hidden border-s-[3px] hover:shadow-md transition-all duration-200 ease-slide',
                severityAccent[rule.severity] || 'border-l-hairline',
              )}
            >
              <div className="flex items-start gap-3 px-4 py-3">
                <Badge color={severityColors[rule.severity]} className="shrink-0 mt-0.5">
                  {t(severityLabels[rule.severity])}
                </Badge>
                <p className="flex-1 min-w-0 text-sm text-fg-body leading-relaxed line-clamp-3 max-w-3xl">
                  {rule.rule_text}
                </p>
                {!readOnly && (
                  <div className="flex items-center gap-1.5 shrink-0 ms-auto">
                    <Button
                      variant="outline"
                      size="small"
                      icon={<PiPencilSimpleLight className="w-4 h-4" />}
                      onClick={(e) => { e.stopPropagation(); handleEdit(rule); }}
                    >
                      {t('Edit')}
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      icon={<PiTrashLight className="w-4 h-4" />}
                      onClick={(e) => { e.stopPropagation(); handleDelete(rule.uuid); }}
                    >
                      {t('Delete')}
                    </Button>
                  </div>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      </div>{/* end content area */}

      <FeatureTour
        steps={globalRulesTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default ReviewGlobalRules;
